import React, { useMemo } from "react";
import { Link } from "react-router-dom";
import { AlertTriangle, Sparkles, ArrowRight } from "lucide-react";
import { useAuth } from "@/context/AuthContext";

/**
 * Persistent banner showing trial-days remaining. Rendered on every
 * page below the topbar. Hides itself once a subscription is Active,
 * Cancelled or missing.
 */
export default function TrialBanner() {
  const { user, tenant } = useAuth();

  const info = useMemo(() => {
    if (!tenant) return null;
    const status = String(tenant.subscription_status || "").toLowerCase();
    const now = new Date();
    const exp = tenant.trial_expires_at;
    if (status !== "trial") {
      if (status === "suspended") {
        return {
          tone: "danger",
          title: "Workspace suspended",
          body: "Your trial has ended. Subscribe to restore access.",
        };
      }
      return null;
    }
    // Trial without an expiry date (e.g. admin-enrolled) still invites subscribing.
    if (!exp) {
      return {
        tone: "info",
        title: "Trial active",
        body: `Subscribe to keep ${tenant.company_name || "your workspace"} active with full module access.`,
      };
    }
    const expDt = new Date(exp);
    const msPerDay = 86400_000;
    const days = Math.ceil((expDt - now) / msPerDay);
    if (days <= 0) {
      return {
        tone: "danger",
        title: "Trial expired",
        body: "Your 30-day trial has ended. Access is being suspended.",
      };
    }
    if (days <= 5) {
      return {
        tone: "warning",
        title: `Trial ends in ${days} day${days === 1 ? "" : "s"}`,
        body: `Subscribe to keep ${tenant.company_name || "your workspace"} active.`,
      };
    }
    return {
      tone: "info",
      title: `Trial: ${days} days left`,
      body: `Full access to every module until ${expDt.toLocaleDateString()}.`,
    };
  }, [tenant]);

  if (!user || !info) return null;

  const styles =
    info.tone === "danger"
      ? "border-rose-500/50 bg-rose-50 text-rose-800 dark:bg-rose-950/30 dark:text-rose-200"
      : info.tone === "warning"
      ? "border-amber-500/50 bg-amber-50 text-amber-800 dark:bg-amber-950/30 dark:text-amber-200"
      : "border-brand/40 bg-brand/5 text-foreground";

  const Icon = info.tone === "info" ? Sparkles : AlertTriangle;

  return (
    <div
      data-testid="trial-banner"
      className={`border ${styles} rounded-md px-3 py-[10px] flex items-center gap-3 text-xs sm:text-sm`}
    >
      <Icon className="h-4 w-4 flex-shrink-0" />
      <div className="flex-1 min-w-0">
        <span className="font-semibold">{info.title}</span>
        <span className="ml-2 opacity-80">{info.body}</span>
      </div>
      <Link
        to="/subscribe"
        data-testid="trial-subscribe-link"
        className="flex-shrink-0 inline-flex items-center gap-1 rounded-full bg-brand text-background font-semibold px-3 py-1 text-xs hover:opacity-90 transition-opacity"
      >
        Subscribe now <ArrowRight className="h-3 w-3" />
      </Link>
    </div>
  );
}
